/* Add contract, in Legal: the window both looks open from the Contracts page
 * (Chris's walk-through, 2026-10-19). Same choices as a recap's contract:
 * our company, the other party, the status and the file, plus the expiry,
 * which the AI suggests from the file. Saving gives the next contract number.
 *
 * State and server calls live in useAddContract.
 *
 * IMT copy of the HMS window: plain labelled controls inside IMT's Field
 * (IMT has no ui/fields), and IMT HMSModal's width (no modal sizes or icon).
 */
import React from 'react';
import PropTypes from 'prop-types';

import HMSModal from '../ui/HMSModal';
import HMSButton from '../ui/HMSButton';
import HMSPartnerSearch from '../ui/HMSPartnerSearch';
import Field from '../ui/Field';
import DatePicker from '../ui/DatePicker';
import { CONTRACT_STATUS_OPTIONS } from '../../services/legalContractService';
import useAddContract from './useAddContract';
import { CONTRACT_FILE_ACCEPT } from './useRecapContract';
import { formatDay } from './contractExpiry';
import './AddContractModal.css';

function ReadLine({ draft, reading, expirySource }) {
  if (!draft) return null;
  if (reading) {
    return <p className="acm-read" role="status">Reading the expiry date from the file…</p>;
  }
  if (draft.read_status === 'failed') {
    // One plain sentence: the reader's own error is for the logs, not the person.
    return <p className="acm-read acm-read--muted" role="status">The expiry date could not be read from this file. Type it in below.</p>;
  }
  const s = draft.suggestion;
  if (draft.read_status === 'completed' && s && (s.expiry_date || s.no_expiry)) {
    const what = s.no_expiry ? 'no expiry / rolls over' : formatDay(s.expiry_date);
    return (
      <p className="acm-read acm-read--found" role="status">
        <strong>Suggested from the file: {what}.</strong>
        {s.basis ? <> It says: “{s.basis}”</> : null}
        {expirySource === 'person' ? ' You entered your own answer below.' : ' Check it below and correct it if needed.'}
      </p>
    );
  }
  if (draft.read_status === 'completed') {
    return <p className="acm-read acm-read--muted" role="status">The file does not say when it expires. Enter the date below.</p>;
  }
  return null;
}

// The empty option of "Our company": what to do, or why the list is empty.
export const companyPrompt = (entities, loaded) => {
  if (entities.length) return 'Choose our company…';
  return loaded ? 'The list of our companies could not load. Reload the page.' : 'Loading companies…';
};

ReadLine.propTypes = {
  draft: PropTypes.object,
  reading: PropTypes.bool,
  expirySource: PropTypes.string,
};

export default function AddContractModal({ isOpen, onClose, onAdded }) {
  const add = useAddContract({ isOpen, onAdded });
  const { form, set, entities, draft, uploading, saving, error } = add;
  const busy = saving || uploading;

  const footer = (
    <>
      <HMSButton variant="secondary" onClick={onClose} disabled={saving}>Cancel</HMSButton>
      <HMSButton variant="primary" onClick={add.submit} disabled={busy}>
        {saving ? 'Adding...' : 'Add contract'}
      </HMSButton>
    </>
  );

  return (
    <HMSModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add contract"
      width={560}
      busy={saving}
      showClose
      footer={footer}
      className="add-contract-modal"
    >
      <p className="acm-intro">
        Upload a contract that does not come from a deal. It gets the next contract number.
      </p>

      <label className="acm-file">
        <span className="acm-legend">Contract file</span>
        <span className="acm-help">PDF, Word, image or text, up to 25 MB.</span>
        <input
          type="file"
          accept={CONTRACT_FILE_ACCEPT}
          aria-label="Contract file"
          onChange={(e) => add.chooseFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
          disabled={busy}
        />
      </label>
      {uploading && <p className="acm-read" role="status">Adding the file…</p>}
      <ReadLine draft={draft} reading={add.reading} expirySource={add.expirySource} />

      <div className="acm-grid">
        <Field label="Our company" required>
          <select
            aria-label="Our company"
            value={form.entity}
            onChange={(e) => set('entity', e.target.value)}
            disabled={saving}
          >
            <option value="">{companyPrompt(entities, add.entitiesLoaded)}</option>
            {entities.map((entity) => (
              <option key={entity.value} value={entity.value}>{entity.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Status">
          <select
            aria-label="Status"
            value={form.status}
            onChange={(e) => set('status', e.target.value)}
            disabled={saving}
          >
            {CONTRACT_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      </div>

      <div className="acm-party">
        <span className="acm-legend">Other party</span>
        {form.notInCrm ? (
          <Field label="Other party's name" hint='Shown on the contract as "not in CRM".'>
            <input
              type="text"
              aria-label="Other party's name"
              value={form.typedParty}
              onChange={(e) => set('typedParty', e.target.value)}
              disabled={saving}
              maxLength={255}
            />
          </Field>
        ) : (
          <HMSPartnerSearch
            value={form.partner}
            onChange={(partner) => set('partner', partner)}
            placeholder="Search CRM by name…"
            disabled={saving}
          />
        )}
        <label className="acm-check">
          <input
            type="checkbox"
            checked={form.notInCrm}
            onChange={(e) => add.setNotInCrm(e.target.checked)}
            disabled={saving}
          />
          <span>Not in CRM: type their name instead</span>
        </label>
      </div>

      <div className="acm-expiry">
        <Field
          label="Expiry date"
          required={!form.noExpiry}
          hint="Reminders go to you and every Legal Approver 90, 30 and 7 days before."
        >
          <DatePicker
            id="add-contract-expiry-date"
            name="expiry_date"
            value={form.expiryDate}
            onChange={(e) => add.setExpiryDate(e.target.value)}
            format="YYYY-MM-DD"
            disabled={saving || form.noExpiry}
            placeholder="Select date"
          />
        </Field>
        <label className="acm-check">
          <input
            type="checkbox"
            checked={form.noExpiry}
            onChange={(e) => add.setNoExpiry(e.target.checked)}
            disabled={saving}
          />
          <span>No expiry / rolls over</span>
        </label>
      </div>

      {error && <p className="acm-error" role="alert">{error}</p>}
    </HMSModal>
  );
}

AddContractModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdded: PropTypes.func,
};
